import '../../i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, TrendingUp, Map, PieChart, Award, Users, Brain, ArrowUpRight } from 'lucide-react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

const TimelineNavigation = () => {
  const [activeSection, setActiveSection] = useState('metrics');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const sections = useMemo(
    () => [
      { id: 'metrics', label: 'Overview', icon: BarChart3, description: 'KPIs & completion' },
      { id: 'performance', label: 'Performance', icon: TrendingUp, description: 'Trend + format mix' },
      { id: 'heatmap', label: 'Heatmap', icon: Map, description: 'Consistency by day' },
      { id: 'breakdown', label: 'Breakdown', icon: PieChart, description: 'Distribution & drilldown' },
      { id: 'achievements', label: 'Achievements', icon: Award, description: 'Progress & milestones' },
      { id: 'comparative', label: 'Comparative', icon: Users, description: 'Baseline vs recent' },
      { id: 'insights', label: 'Insights', icon: Brain, description: 'AI next steps' },
    ],
    []
  );

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      setScrollProgress(total > 0 ? (doc.scrollTop / total) * 100 : 0);

      setIsScrolling(true);
      window.clearTimeout(onScroll._t);
      onScroll._t = window.setTimeout(() => setIsScrolling(false), 120);

      // detect active section (nearest top)
      const offsets = sections
        .map((s) => {
          const el = document.getElementById(s.id);
          if (!el) return null;
          const rect = el.getBoundingClientRect();
          return { id: s.id, top: rect.top };
        })
        .filter(Boolean);

      const candidates = offsets.filter((o) => o.top <= 140).sort((a, b) => b.top - a.top);
      if (candidates.length) setActiveSection(candidates[0].id);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="fixed right-5 top-24 z-40 hidden lg:block">
      <div className="relative w-14">
        {/* Progress rail */}
        <div className="absolute left-1/2 top-0 h-[560px] w-[2px] -translate-x-1/2 rounded-full bg-white/8 overflow-hidden">
          <div
            className="w-full rounded-full bg-gradient-to-b from-violet-400/70 via-sky-400/70 to-emerald-400/70 transition-all"
            style={{ height: `${scrollProgress}%` }}
          />
        </div>

        {/* Nav */}
        <div className="relative flex flex-col items-center gap-3 pt-2">
          {sections.map((s, idx) => {
            const ActiveIcon = s.icon;
            const active = activeSection === s.id;
            return (
              <Tippy
                key={s.id}
                content={
                  <div className="text-xs">
                    <div className="font-semibold text-white">{s.label}</div>
                    <div className="text-white/65 mt-0.5">{s.description}</div>
                  </div>
                }
                className="rounded-2xl border border-white/10 bg-[#080a0f]/95 px-3 py-2 shadow-2xl backdrop-blur-xl"
                placement="left"
              >
                <button
                  onClick={() => scrollTo(s.id)}
                  className={[
                    "relative h-11 w-11 rounded-2xl border transition backdrop-blur-xl",
                    active
                      ? "border-white/18 bg-white/[0.10] shadow-[0_0_0_1px_rgba(255,255,255,0.10)]"
                      : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]",
                  ].join(" ")}
                >
                  <ActiveIcon className={active ? "h-5 w-5 text-white mx-auto" : "h-5 w-5 text-white/70 mx-auto"} />
                  {active ? (
                    <span className="pointer-events-none absolute -left-1 -top-1 h-2 w-2 rounded-full bg-emerald-300 shadow-[0_0_16px_rgba(52,211,153,0.55)]" />
                  ) : null}
                </button>
              </Tippy>
            );
          })}

          {/* Quick jump top */}
          <div className="mt-3">
            <Tippy
              content={<div className="text-xs text-white/80">Back to top</div>}
              className="rounded-2xl border border-white/10 bg-[#080a0f]/95 px-3 py-2 shadow-2xl backdrop-blur-xl"
              placement="left"
            >
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="h-10 w-10 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition"
              >
                <ArrowUpRight className="h-5 w-5 text-white/70 mx-auto rotate-[-90deg]" />
              </button>
            </Tippy>
          </div>

          {/* subtle scroll indicator */}
          <div className={["mt-2 text-[10px] text-white/40 transition", isScrolling ? "opacity-100" : "opacity-0"].join(" ")}>
            {Math.round(scrollProgress)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineNavigation;
